import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Tag,
  Space,
  Tooltip,
  Select,
  DatePicker,
  message,
  Switch,
  Popconfirm,
  Typography,
  Tabs,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  FilterFilled,
} from "@ant-design/icons";
import Main from "../components/layout/Main";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import useDebounce from "../hooks/useDebounce";
import apiPath from "../constants/apiPath";
import moment from "moment";
import CURRENCY_OPTIONS from "../helper/currencyOption";

import CheckImgIcon from "../assets/images/activee-main-icon.svg";
import SuspendIcon from "../assets/images/suspend-main-icon.svg";
import { Eye } from "lucide-react";
import lang from "../helper/lang";

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;
const { RangePicker } = DatePicker;

function SubscriptionPriceManager() {
  const { request } = useRequest();
  const requestRef = useRef(request);

  // State management
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState("add"); // add, edit
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    sort: "newest",
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [filtersVisible, setFiltersVisible] = useState(true);

  // Loading states for different operations
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState({});
  const [deletedFeatureIds, setDeletedFeatureIds] = useState([]);
  const [activeTab, setActiveTab] = useState("users");

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  const debouncedSearchText = useDebounce(searchText, 500);

  // Fetch subscriptions
  const fetchSubscriptions = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();

    // Add sort parameter
    if (filters.sort) {
      params.append("sort", filters.sort);
    }

    // Add search parameter
    if (debouncedSearchText) {
      params.append("search", debouncedSearchText);
    }

    // Add pagination parameters
    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);

    requestRef.current({
      url: `/api/${activeTab === "users" ? apiPath.userSubscriptions : apiPath.subscriptions}?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);

        if (data?.status === 200 && data?.data) {
          // Handle the response structure based on your API specification
          setSubscriptions(Array.isArray(data.data) ? data.data : []);

          // Handle pagination from your API response
          if (data.pagination) {
            setPagination((prev) => ({
              ...prev,
              total: data.pagination.totalCount || 0,
              current: Number(data.pagination.currentPage) || prev.current,
              pageSize: data.pagination.limit || prev.pageSize,
            }));
          }
        } else {
          setSubscriptions([]);
          setPagination((prev) => ({ ...prev, total: 0 }));
          ShowToast(
            data?.message || "Failed to fetch subscriptions",
            Severty.ERROR,
          );
        }
      },
      onError: (error) => {
        setLoading(false);
        setSubscriptions([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        ShowToast("Failed to fetch subscriptions", Severty.ERROR);
      },
    });
  }, [filters, debouncedSearchText, pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  // Handlers
  const handleTableChange = (paginationInfo, filters, sorter) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleAddSubscription = () => {
    setModalType("add");
    setSelectedSubscription(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditSubscription = (subscription) => {
    setLoading(true);
    requestRef.current({
      url: `/api/${activeTab === "users" ? apiPath.userSubscriptions : apiPath.subscriptions}/${subscription.id}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        if (data?.status === 200 && data?.data) {
          const detail = data.data;
          setSelectedSubscription(detail);
          setModalType("edit");
          setModalVisible(true);
          setDeletedFeatureIds([]);

          form.setFieldsValue({
            name: detail.name,
            duration_days: detail.duration_days,
            description: detail.description || "",
            price: detail.price,
            currency: detail.currency,
            status: detail.is_active,
            is_published: detail.is_published,
            features: detail.features || [],
            daily_transaction_limit: detail.daily_transaction_limit,
            monthly_transaction_limit: detail.monthly_transaction_limit,
            fees_percentage: detail.fees_percentage,
            daily_transaction_count: detail.daily_transaction_count,
            is_monthly_trial: detail.is_monthly_trial,
            monthly_trial_days: detail.monthly_trial_days,
            is_transaction_trial: detail.is_transaction_trial,
            transaction_trial_days: detail.transaction_trial_days,
          });
        } else {
          ShowToast(data?.message || "Failed to fetch subscription detail", Severty.ERROR);
        }
      },
      onError: (error) => {
        setLoading(false);
        ShowToast("Failed to fetch subscription detail", Severty.ERROR);
      }
    });
  };

  // Create or update subscription
  const handleFormSubmit = async (values) => {
    try {
      const formattedFeatures = (values.features || []).map((f) => {
        // Existing feature
        if (f.id) {
          return {
            id: f.id,
            description: f.description,
          };
        }

        // New feature (NO id)
        return {
          description: f.description,
        };
      });
      // const [startDate, endDate] = values.effective_dates || [];

      const subscriptionData = {
        name: values.name,
        duration_days: values.duration_days,
        price: parseFloat(values.price),
        currency: values.currency,
        description: values.description,
        features: formattedFeatures,
        is_active: values.status,
        is_published: values.is_published || false,
        daily_transaction_limit: values.daily_transaction_limit,
        monthly_transaction_limit: values.monthly_transaction_limit,
        fees_percentage: values.fees_percentage,
        daily_transaction_count: Number(
          String(values.daily_transaction_count).replace(",", "."),
        ),
        is_monthly_trial: values.is_monthly_trial || false,
        monthly_trial_days: values.monthly_trial_days || 0,
        is_transaction_trial: values.is_transaction_trial || false,
        transaction_trial_days: values.transaction_trial_days || 0,
      };

      if (modalType === "add") {
        setCreateLoading(true);
        requestRef.current({
          url: `/api/${activeTab === "users" ? apiPath.userSubscriptions : apiPath.subscriptions}`,
          method: "POST",
          data: subscriptionData,
          onSuccess: (data) => {
            if (data?.status === 201) {
              ShowToast("Subscription created successfully", Severty.SUCCESS);
              setModalVisible(false);
              fetchSubscriptions();
            } else {
              ShowToast(
                data?.message || "Failed to create subscription",
                Severty.ERROR,
              );
            }
            setCreateLoading(false);
          },
          onError: (error) => {
            console.log("its error", error?.response?.data?.message);
            ShowToast(
              error?.response?.data?.message || "Failed to create subscription",
              Severty.ERROR,
            );
            setCreateLoading(false);
          },
        });
      } else {
        setUpdateLoading(true);
        requestRef.current({
          url: `/api/${activeTab === "users" ? apiPath.userSubscriptions : apiPath.subscriptions}/${selectedSubscription.id}`,
          method: "PUT",
          data: subscriptionData,
          onSuccess: (data) => {
            if (data?.status === 200) {
              ShowToast("Subscription updated successfully", Severty.SUCCESS);
              setModalVisible(false);
              fetchSubscriptions();
            } else {
              ShowToast(
                data?.message || "Failed to update subscription",
                Severty.ERROR,
              );
            }
            setUpdateLoading(false);
          },
          onError: (error) => {
            ShowToast(
              error?.response?.data?.message || "Failed to update subscription",
              Severty.ERROR,
            );
            setUpdateLoading(false);
          },
        });
      }
    } catch (error) {
      ShowToast("Failed to save subscription", Severty.ERROR);
      if (modalType === "add") {
        setCreateLoading(false);
      } else {
        setUpdateLoading(false);
      }
    }
  };

  // Delete subscription
  const handleDeleteSubscription = async (subscription) => {
    try {
      setDeleteLoading((prev) => ({ ...prev, [subscription.id]: true }));
      requestRef.current({
        url: `/api/${apiPath.subscriptions}/${subscription.id}`,
        method: "DELETE",
        onSuccess: (data) => {
          if (data?.status === 200) {
            ShowToast("Subscription deleted successfully", Severty.SUCCESS);
            fetchSubscriptions();
          } else {
            ShowToast(
              data?.message || "Failed to delete subscription",
              Severty.ERROR,
            );
          }
          setDeleteLoading((prev) => ({ ...prev, [subscription.id]: false }));
        },
        onError: (error) => {
          ShowToast(
            error?.message || "Failed to delete subscription",
            Severty.ERROR,
          );
          setDeleteLoading((prev) => ({ ...prev, [subscription.id]: false }));
        },
      });
    } catch (error) {
      ShowToast("Failed to delete subscription", Severty.ERROR);
      setDeleteLoading((prev) => ({ ...prev, [subscription.id]: false }));
    }
  };

  // Toggle subscription status
  const toggleSubscriptionStatus = async (subscription, newStatus) => {
    try {
      requestRef.current({
        url: `/api/${activeTab === "users" ? apiPath.userSubscriptions : apiPath.subscriptions}/${subscription.id}`,
        method: "PUT",
        data: {
          is_active: newStatus,
        },
        onSuccess: (data) => {
          if (data?.status === 200) {
            ShowToast(
              `Subscription ${newStatus === "ACTIVE" ? "activated" : "deactivated"
              } successfully`,
              Severty.SUCCESS,
            );
            fetchSubscriptions();
          } else {
            ShowToast(
              data?.message || "Failed to update subscription status",
              Severty.ERROR,
            );
          }
        },
        onError: (error) => {
          ShowToast("Failed to update subscription status", Severty.ERROR);
        },
      });
    } catch (error) {
      ShowToast("Failed to update subscription status", Severty.ERROR);
    }
  };

  const handleViewSubscription = (subscription) => {
    setLoading(true);
    requestRef.current({
      url: `/api/${activeTab === "users" ? apiPath.userSubscriptions : apiPath.subscriptions}/${subscription.id}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        if (data?.status === 200 && data?.data) {
          setSelectedSubscription(data.data);
          setModalType("view");
          setModalVisible(true);
        } else {
          ShowToast(data?.message || "Failed to fetch subscription detail", Severty.ERROR);
        }
      },
      onError: (error) => {
        setLoading(false);
        ShowToast("Failed to fetch subscription detail", Severty.ERROR);
      }
    });
  };

  // Table columns
  const getColumns = () => [
    {
      title: "S. No.",
      key: "serial_no",
      width: 80,
      render: (_, __, index) => {
        const serialNumber =
          (pagination.current - 1) * pagination.pageSize + index + 1;
        return <Text>{serialNumber}</Text>;
      },
    },
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      render: (id) => <Text code>{id}</Text>,
    },
    {
      title: "Package Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name) => <strong>{name}</strong>,
    },
    {
      title: "Duration",
      dataIndex: "duration_days",
      key: "duration_days",
      render: (duration) => <span>{duration} days</span>,
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (price, record) => (
        <span>
          {record.currency} {price}
        </span>
      ),
    },
    // {
    //   title: "Price",
    //   dataIndex: "price",
    //   key: "price",
    //   render: (price, record) => (
    //     <span>{price ? `${record.currency} ${price}` : "-"}</span>
    //   ),
    // },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      // render: (currency) => <span>{currency}</span>,
    },
    {
      title: "Daily Transaction Count",
      dataIndex: "daily_transaction_count",
      key: "daily_transaction_count",
      render: (value) => <span>{value ?? "-"}</span>,
    },
    {
      title: "Daily Transaction Limit",
      dataIndex: "daily_transaction_limit",
      key: "daily_transaction_limit",
      render: (value) => <span>{value ?? "-"}</span>,
    },
    {
      title: "Monthly Transaction Limit",
      dataIndex: "monthly_transaction_limit",
      key: "monthly_transaction_limit",
      render: (value) => <span>{value ?? "-"}</span>,
    },
    {
      title: "Fees Percentage",
      dataIndex: "fees_percentage",
      key: "fees_percentage",
      render: (value) => <span>{value ?? "-"}%</span>,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (is_active) => (
        <Tag color={is_active ? "green" : "red"}>
          {is_active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Publishing",
      dataIndex: "is_published",
      key: "is_published",
      width: 120,
      render: (is_published) => (
        <Tag color={is_published ? "blue" : "orange"}>
          {is_published ? "Published" : "Draft"}
        </Tag>
      ),
    },
    // {
    //   title: "Created on",
    //   dataIndex: "created_at",
    //   key: "created_at",
    //   width: 200,
    //   render: (date) => new Date(date).toLocaleString(),
    // },
    // {
    //   title: "Last Updated",
    //   dataIndex: "updated_at",
    //   key: "updated_at",
    //   width: 200,
    //   render: (date) => new Date(date).toLocaleString(),
    // },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={lang("View_Profile") || "View Profile"}>
            <Button
              className="button-action-small"
              icon={<Eye />}
              onClick={() => handleViewSubscription(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              className="button-action-small"
              icon={<EditOutlined />}
              onClick={() => handleEditSubscription(record)}
            />
          </Tooltip>
          <Tooltip title={record.is_active ? "Deactivate" : "Activate"}>
            <Button
              className="button-action-small"
              icon={
                <img
                  src={record.is_active ? SuspendIcon : CheckImgIcon}
                  alt="status"
                />
              }
              style={{ color: "#52c41a" }}
              type={record.is_active ? "default" : "primary"}
              onClick={() =>
                toggleSubscriptionStatus(
                  record,
                  record.is_active ? false : true,
                )
              }
            >
              {/* {record.is_active ? "Deactivate" : "Activate"} */}
            </Button>
          </Tooltip>
          <Popconfirm
            title="Delete Subscription"
            description="Are you sure you want to delete this subscription?"
            onConfirm={() => handleDeleteSubscription(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              className="button-action-small"
              icon={<DeleteOutlined />}
              danger
              loading={deleteLoading[record.id]}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div>
              <span>Subscription Price Manager</span>
              <p>Manage pricing for all predefined subscription packages</p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                icon={<FilterFilled />}
                onClick={() => setFiltersVisible((prev) => !prev)}
              >
                {filtersVisible ? "Hide Filters" : "Show Filters"}
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddSubscription}
              >
                Add Subscription
              </Button>
            </div>
          </div>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            setModalVisible(false);
            setModalType("add");
            setFilters({
              status: null,
              sort: null,
              dateRange: null,
            });
            setSearchText("");
            setPagination((prev) => ({ ...prev, current: 1 }));
          }}
          items={[
            { key: "users", label: "Users" },
            { key: "merchants", label: "Merchants" },
          ]}
        />

        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
            }}
          >
            {filtersVisible && (
              <>
                <Search
                  placeholder="Search subscriptions"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={handleSearch}
                  style={{ minWidth: 200, flex: "1 1 200px" }}
                />
                <Select
                  placeholder="Sort By"
                  style={{ minWidth: 120, flex: "1 1 120px" }}
                  value={filters.sort}
                  onChange={(value) => handleFilterChange("sort", value)}
                  allowClear
                >
                  <Option value="newest">Newest</Option>
                  <Option value="oldest">Oldest</Option>
                </Select>
              </>
            )}
          </div>
        </div>
        {/* Subscriptions Table */}
        <div className="table-responsive">
          <Table
            columns={getColumns()}
            dataSource={subscriptions}
            rowKey="id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total || 0,
              showSizeChanger: true,
              showQuickJumper: false,
              showTotal: (total, range) =>
                `Showing ${range[0]}-${range[1]} of ${total} subscriptions`,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            loading={loading}
            onChange={handleTableChange}
          />
        </div>
      </Card>

      {/* Edit Subscription Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {modalType === "add"
              ? "Add Subscription Package"
              : modalType === "view"
                ? "View Subscription Package"
                : "Edit Subscription Package"}
          </div>
        }
        open={modalVisible}
        onCancel={() => {
          setDeletedFeatureIds([]);
          setModalVisible(false);
        }}
        footer={null}
        width={600}
        confirmLoading={createLoading || updateLoading}
      >
        {modalType === "view" && selectedSubscription && (
          <div>
            <Card
              style={{
                borderRadius: 16,
                background: "linear-gradient(135deg, #f6f9ff, #ffffff)",
                boxShadow: "0 8px 30px rgba(0,0,0,0.06)",
              }}
            >
              <Row justify="space-between" align="middle">
                <Col>
                  <h2 style={{ margin: 0 }}>{selectedSubscription.name}</h2>
                  <Text type="secondary">
                    {selectedSubscription.description || "No description"}
                  </Text>
                </Col>
                <Tag
                  color={selectedSubscription.is_active ? "green" : "red"}
                  style={{ fontSize: 14, padding: "6px 14px", borderRadius: 20 }}
                >
                  {selectedSubscription.is_active ? "Active" : "Inactive"}
                </Tag>
              </Row>

              <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                {[
                  { label: "Price", value: `${selectedSubscription.currency} ${selectedSubscription.price}` },
                  { label: "Duration", value: `${selectedSubscription.duration_days} days` },
                  { label: "Fees", value: `${selectedSubscription.fees_percentage}%` },
                  { label: "Daily Limit", value: selectedSubscription.daily_transaction_limit },
                  { label: "Monthly Limit", value: selectedSubscription.monthly_transaction_limit },
                  { label: "Monthly Trial", value: selectedSubscription.is_monthly_trial ? `${selectedSubscription.monthly_trial_days} days` : "Disabled" },
                  { label: "Transaction Trial", value: selectedSubscription.is_transaction_trial ? `${selectedSubscription.transaction_trial_days} days` : "Disabled" },
                ].map((item) => (
                  <Col span={12}>
                    <Card
                      size="small"
                      style={{
                        borderRadius: 12,
                        background: "#fafafa",
                      }}
                    >
                      <Text type="secondary">{item.label}</Text>
                      <h3 style={{ marginTop: 4 }}>{item.value || "-"}</h3>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>

            <Card
              title="Features"
              style={{ marginTop: 20, borderRadius: 16 }}
            >
              <Row gutter={[10, 10]}>
                {selectedSubscription.features?.map((f) => (
                  <Col key={f.id}>
                    <Tag
                      color="blue"
                      style={{
                        padding: "6px 12px",
                        borderRadius: 20,
                        fontSize: 13,
                      }}
                    >
                      {f.description}
                    </Tag>
                  </Col>
                ))}
              </Row>
            </Card>

            <div style={{ textAlign: "right", marginTop: 20 }}>
              <Button size="large" onClick={() => setModalVisible(false)}>
                Close
              </Button>
            </div>
          </div>
        )}
        {modalType !== "view" && (
          <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
            <Card title="Package Details" style={{ marginBottom: 16, borderRadius: 14 }}>
              <Row gutter={16}>
                <Col span={12}>
                      <Form.Item
                        name="name"
                        label="Package Name"
                        rules={[
                          {
                            required: true,
                            message: "Please enter package name",
                          },
                          {
                            pattern: /^[A-Za-z0-9 _-]+$/,
                            message: "Special characters are not allowed",
                          },
                        ]}
                      >
                        <Input placeholder="Enter package name" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="duration_days"
                        label="Duration"
                        rules={[
                          {
                            required: true,
                            message: "Please select duration",
                          },
                        ]}
                      >
                        <Select placeholder="Select duration">
                          <Option value="30">30 days</Option>
                          <Option value="60">60 days</Option>
                          <Option value="90">90 days</Option>
                          <Option value="180">180 days</Option>
                          <Option value="360">360 days</Option>
                          <Option value="84">Quarterly</Option>
                          <Option value="365">Yearly</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="currency" label="Currency">
                        <Select
                          showSearch
                          placeholder="Select currency"
                          optionFilterProp="label"
                          value={form.getFieldValue("currency")}
                          defaultValue="NZD"
                          filterOption={(input, option) =>
                            option.label
                              .toLowerCase()
                              .includes(input.toLowerCase()) ||
                            option.value
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          <Option value="NZD" label="NZD">
                            NZD
                          </Option>
                          {/* {CURRENCY_OPTIONS.map((currency) => (
                        <Option key={currency.value} value={currency.value} label={currency.label}>
                          {currency.label}
                        </Option>
                      ))} */}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>

            <Card
              title="Pricing & Limits"
              style={{ marginBottom: 16, borderRadius: 14 }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="price"
                    label="Monthly Fees ($)"
                    rules={[
                      {
                        required: true,
                        message: "Please enter Monthly Fees",
                      },
                      {
                        pattern: /^\d+(\.\d{1,2})?$/,
                        message: "Please enter a valid Monthly Fees",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter Monthly Fees"
                      type="number"
                      step="0.01"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="fees_percentage"
                    label="Transaction Fees Percentage (%)"
                    rules={[
                      {
                        required: true,
                        message: "Please enter Transaction Fees Percentage",
                      },
                      {
                        pattern: /^\d+(\.\d{1,2})?$/,
                        message: "Please enter a valid Transaction Fees Percentage",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter Transaction Fees Percentage"
                      type="number"
                      step="0.01"
                    />
                  </Form.Item>
                </Col>
                {/* <Col span={12}>
              <Form.Item
                name="discounted_price"
                label="Discounted Price (Optional)"
                rules={[
                  {
                    pattern: /^\d+(\.\d{1,2})?$/,
                    message: "Please enter a valid price",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const basePrice = getFieldValue("base_price");

                      // If discounted price is empty, it's valid
                      if (!value || !basePrice) {
                        return Promise.resolve();
                      }

                      if (parseFloat(value) > parseFloat(basePrice)) {
                        return Promise.reject(
                          new Error("Discounted price cannot be greater than base price")
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  placeholder="Enter discounted price"
                  type="number"
                  step="0.01"
                />
              </Form.Item>
            </Col> */}
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="daily_transaction_limit"
                    label="Daily Transaction Limit ($)"
                    rules={[
                      {
                        required: true,
                        message: "Please enter daily transaction limit",
                      },
                      {
                        pattern: /^\d+(\.\d{1,2})?$/,
                        message: "Please enter a valid daily transaction limit",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter daily transaction limit"
                      type="number"
                      step="0.01"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="monthly_transaction_limit"
                    label="Monthly Transaction Limit ($)"
                    rules={[
                      {
                        required: true,
                        message: "Please enter monthly transaction limit",
                      },
                      {
                        pattern: /^\d+(\.\d{1,2})?$/,
                        message: "Please enter a valid monthly transaction limit",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter monthly transaction limit"
                      type="number"
                      step="0.01"
                    />
                  </Form.Item>
                </Col>
                {/* <Col span={12}>
              <Form.Item
                name="discounted_price"
                label="Discounted Price (Optional)"
                rules={[
                  {
                    pattern: /^\d+(\.\d{1,2})?$/,
                    message: "Please enter a valid price",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const basePrice = getFieldValue("base_price");

                      // If discounted price is empty, it's valid
                      if (!value || !basePrice) {
                        return Promise.resolve();
                      }

                      if (parseFloat(value) > parseFloat(basePrice)) {
                        return Promise.reject(
                          new Error("Discounted price cannot be greater than base price")
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  placeholder="Enter discounted price"
                  type="number"
                  step="0.01"
                />
              </Form.Item>
            </Col> */}
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="daily_transaction_count"
                    label="Daily Transaction Count"
                    rules={[
                      {
                        required: true,
                        message: "Please enter daily transaction count",
                      },
                      {
                        pattern: /^\d+$/,
                        message: "Please enter a valid daily transaction count",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter daily transaction count"
                      type="text"
                      inputMode="decimal"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* <Form.Item
            name="effective_dates"
            label="Effective Date Range (Optional)"
          >
            <RangePicker style={{ width: "100%" }} disabledDate={(current) => current && current < moment().startOf("day")} />
          </Form.Item> */}

            {/* <Form.Item
            name="max_users"
            label="Max Users"
            rules={[
              {
                pattern: /^\d+$/,
                message: "Please enter a valid whole number",
              },
            ]}
          >
            <Input
              placeholder="Enter max users (0 = unlimited)"
              type="number"
              min={0}
            />
          </Form.Item> */}
            <Card title="Free Trial Settings" style={{ marginBottom: 16, borderRadius: 14 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="is_monthly_trial" label="Monthly Fee Free Trial" valuePropName="checked">
                    <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => prevValues.is_monthly_trial !== currentValues.is_monthly_trial}
                  >
                    {({ getFieldValue }) =>
                      getFieldValue("is_monthly_trial") ? (
                        <Form.Item
                          name="monthly_trial_days"
                          label="Monthly Trial Duration (Days)"
                          rules={[{ required: true, message: "Enter duration" }]}
                        >
                          <Input type="number" placeholder="Enter days" />
                        </Form.Item>
                      ) : null
                    }
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="is_transaction_trial" label="Transaction Fee Free Trial" valuePropName="checked">
                    <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => prevValues.is_transaction_trial !== currentValues.is_transaction_trial}
                  >
                    {({ getFieldValue }) =>
                      getFieldValue("is_transaction_trial") ? (
                        <Form.Item
                          name="transaction_trial_days"
                          label="Transaction Trial Duration (Days)"
                          rules={[{ required: true, message: "Enter duration" }]}
                        >
                          <Input type="number" placeholder="Enter days" />
                        </Form.Item>
                      ) : null
                    }
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title="Additional Details" style={{ marginBottom: 16, borderRadius: 14 }}>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="description"
                    label="Description"
                    rules={[
                      {
                        required: true,
                        message: "Please enter description",
                      },
                    ]}
                  >
                    <Input placeholder="Enter description" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
            <Card title="Features" style={{ marginBottom: 16, borderRadius: 14 }}>
              <Form.List
                name="features"
                rules={[
                  {
                    validator: async (_, features) => {
                      if (!features || features.length < 2) {
                        return Promise.reject(
                          new Error("At least two features are required"),
                        );
                      }
                    },
                  },
                ]}
              >
                {(fields, { add, remove }, { errors }) => (
                  <>
                    <Row justify="space-between" align="middle">
                      <Col>
                        {/* 👇 Helper / validation text */}
                        <span
                          style={{
                            fontSize: 12,
                            color: errors.length ? "#ff4d4f" : "#8c8c8c",
                          }}
                        >
                          Minimum two features required
                        </span>
                      </Col>

                      <Col>
                        <Button
                          type="dashed"
                          icon={<PlusOutlined />}
                          onClick={() => add()}
                        >
                          Add Feature
                        </Button>
                      </Col>
                    </Row>

                    {fields.map(({ key, name, ...restField }) => (
                      <Card
                        key={key}
                        size="small"
                        style={{ marginTop: 12 }}
                        title={`Feature ${name + 1}`}
                        extra={
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              const features = form.getFieldValue("features");
                              const featureToDelete = features?.[name];

                              if (modalType === "edit" && featureToDelete?.id) {
                                setDeletedFeatureIds((prev) => [
                                  ...prev,
                                  featureToDelete.id,
                                ]);
                              }

                              remove(name);
                            }}
                          />
                        }
                      >
                        <Row gutter={16}>
                          {/* <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, "name"]}
                          label="Feature Name"
                          rules={[
                            { required: true, message: "Enter feature name" },
                          ]}
                        >
                          <Input placeholder="Feature name" />
                        </Form.Item>
                      </Col> */}

                          <Col span={12}>
                            <Form.Item
                              {...restField}
                              name={[name, "description"]}
                              label="Feature Description"
                              rules={[
                                {
                                  required: true,
                                  message: "Enter feature description",
                                },
                              ]}
                            >
                              <Input placeholder="Feature description" />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    ))}
                  </>
                )}
              </Form.List>
            </Card>

            <Card style={{ borderRadius: 14 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="status" valuePropName="checked" label="Active State" style={{ marginBottom: 0 }}>
                    <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="is_published" valuePropName="checked" label="Publish State" style={{ marginBottom: 0 }}>
                    <Switch checkedChildren="Published" unCheckedChildren="Draft" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Form.Item
              shouldUpdate
              style={{ marginTop: 24, marginBottom: 0, textAlign: "right" }}
            >
              {() => {
                const basePrice = form.getFieldValue("base_price");
                const discountedPrice = form.getFieldValue("discounted_price");

                const isInvalidDiscount =
                  basePrice &&
                  discountedPrice &&
                  parseFloat(discountedPrice) > parseFloat(basePrice);

                const hasErrors = form
                  .getFieldsError()
                  .some(({ errors }) => errors.length > 0);

                return (
                  <>
                    <Button
                      style={{ marginRight: 8 }}
                      onClick={() => setModalVisible(false)}
                      disabled={createLoading || updateLoading}
                    >
                      Cancel
                    </Button>

                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={
                        modalType === "add" ? createLoading : updateLoading
                      }
                      disabled={
                        createLoading ||
                        updateLoading ||
                        isInvalidDiscount ||
                        hasErrors
                      }
                    >
                      {modalType === "add" ? "Create" : "Update"}
                    </Button>
                  </>
                );
              }}
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}

export default SubscriptionPriceManager;
